import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocalizationProvider } from './context/LocalizationContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { useUserData } from './hooks/useUserData';
import Dashboard from './components/Dashboard';
import WelcomeScreen from './components/WelcomeScreen';
import VitalsCheck from './components/VitalsCheck';
import Consultation from './components/Consultation';
import EndSession from './components/EndSession';
import LoadingScreen from './components/LoadingScreen';
import Assessment from './components/Assessment';
import AssessmentDetail from './components/Assessment/AssessmentDetail';
import ChatManager from './components/ChatManager';
import './App.css';
import TopBar from './components/shared/TopBar';
import WebSocketStatus from './components/WebSocketStatus';
import backgroundVideo from './assets/videos/background.mp4';
const AppContent = () => {
  const { userData, loading, error } = useUserData();
  const { isLoading: authLoading, error: authError } = useAuth();
  const { setUserData } = useAppContext();
  const lastStoredUserData = useRef(null);

  useEffect(() => {
    if (userData && userData.patientId) {
      if (JSON.stringify(userData) !== JSON.stringify(lastStoredUserData.current)) {
        setUserData(userData);
        lastStoredUserData.current = userData;
      }
    }
  }, [userData, setUserData]);

  // Show loading screen while user data or authentication is loading
  if (loading || authLoading) {
    return <LoadingScreen />;
  }

  // Show authentication error if auth fails
  if (authError) {
    return (
      <div className="app">
        <div className="error-container">
          <h2>Authentication Error</h2>
          <p>{authError}</p>
          <button onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show error if user data fails to load
  if (error) {
    console.error('App error:', error);
  }

  return (
    <div className="app">
      <video
        className="app-video-background"
        autoPlay
        loop
        muted
        playsInline
      >
        <source src={backgroundVideo} type="video/mp4" />
      </video>
      <TopBar />
      <WebSocketStatus />
      <Routes>
        <Route path="/" element={<WelcomeScreen userData={userData} />} />
        <Route path="/dashboard" element={<Dashboard userData={userData} />} />
        <Route path="/welcome" element={<WelcomeScreen userData={userData} />} />
        <Route path="/vitals" element={<VitalsCheck userData={userData} />} />
        <Route path="/consultation/:podId?" element={<Consultation userData={userData} />} />
        <Route path="/assessment" element={<Assessment userData={userData} />} />
        <Route path="/assessment/:type" element={<AssessmentDetail />} />
        <Route path="/chat-manager" element={<ChatManager userData={userData} />} />
        <Route path="/end" element={<EndSession userData={userData} />} />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <LocalizationProvider>
      <AuthProvider>
        <AppProvider>
          <WebSocketProvider>
            <Router>
              <AppContent />
            </Router>
          </WebSocketProvider>
        </AppProvider>
      </AuthProvider>
    </LocalizationProvider>
  );
};

export default App;
